import React, { useEffect, useState } from "react";
import Layout from "../../components/layouts/Layout";
import BreadcrumbBlock from "../../components/BreadcrumbBlock/BreadcrumbBlock";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  Input,
  Select,
  Button,
  Pagination,
  Tooltip,
  Flex,
  Spin,
  notification,
} from "antd";
import { Home2, ArrowSquareDown } from "iconsax-react";
import {
  RobotFilled,
  SearchOutlined,
  EyeOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { mixGetAi, mixPostAi } from "../../services/apiAi";
import dayjs from "dayjs";
import "./TicketList.scss";

interface Conversation {
  conversation_id: string;
  ticket_no: number;
  customer_id: string;
  customer_name: string;
  customer_email: string | null;
  channel: string;
  status: string;
  status_label?: string;
  last_intent: string;
  last_message_at: string;
  created_at: string;
  handler?: string;
  summary?: string;
  last_message?: {
    sender_type: "customer" | "bot" | "human_agent";
    content: string;
    created_at: string;
  };
}

const TicketList: React.FC = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const navigate = useNavigate();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  // Filters
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined,
  );
  const [sortBy, setSortBy] = useState<string>("created_at");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Setup Notification helper
  const [api, contextHolder] = notification.useNotification();
  const openNotification = (
    type: "success" | "error" = "success",
    message = "Request success!",
  ) => {
    api[type]({
      message: message,
      placement: "bottomRight",
      className: "single-message",
    });
  };

  // Authentication check - admin only
  useEffect(() => {
    if (!user || user?.role !== 1) {
      navigate("/");
    }
  }, [user, navigate]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const res = await mixGetAi("/admin/conversations", {});
      if (res && Array.isArray(res)) {
        setConversations(res);
      } else {
        setConversations([]);
      }
    } catch (error) {
      console.error("Error loading conversations:", error);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleResolve = async (convId: string) => {
    try {
      setResolvingId(convId);
      await mixPostAi(`/conversations/${convId}/status`, {
        status: "resolved",
      });
      openNotification("success", "Ticket marked as Resolved");
      fetchConversations();
    } catch (error) {
      console.error("Error resolving conversation:", error);
      openNotification("error", "Failed to resolve conversation");
    } finally {
      setResolvingId(null);
    }
  };

  const formatChicago = (
    dateStr: string,
    formatPattern = "YYYY-MM-DD HH:mm",
  ) => {
    if (!dateStr) return "";
    return dayjs(dateStr).format(formatPattern);
  };

  const formatLastMessageTime = (lastMsgAt: string) => {
    if (!lastMsgAt) return "";
    const now = dayjs();
    const diffMs = now.diff(dayjs(lastMsgAt));
    const diffMins = Math.floor(diffMs / 1000 / 60);
    const hrs = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    if (hrs === 0) return `${mins}m`;
    return `${hrs}h${mins}m`;
  };

  // Filter and sort conversations locally
  const filteredConversations = conversations
    .filter((conv) => {
      // 1. Search text filter
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const matchesSearch =
          String(conv.ticket_no).includes(searchLower) ||
          conv.customer_name?.toLowerCase().includes(searchLower) ||
          (conv.customer_email &&
            conv.customer_email.toLowerCase().includes(searchLower)) ||
          conv.last_intent?.toLowerCase().includes(searchLower) ||
          (conv.handler && conv.handler.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }

      // 2. Status filter
      if (statusFilter) {
        if (statusFilter === "waiting_for_human") {
          return conv.status === "waiting_for_human";
        }
        if (statusFilter === "human_handling") {
          return conv.status === "active";
        }
        if (statusFilter === "resolved") {
          return conv.status === "resolved";
        }
        if (statusFilter === "active") {
          // "active" statusFilter represents "Bot Handling"
          return (
            conv.status !== "waiting_for_human" &&
            conv.status !== "active" &&
            conv.status !== "resolved"
          );
        }
      }

      return true;
    })
    .sort((a, b) => {
      // 3. Sorting
      if (sortBy === "created_at") {
        const dateA = a.created_at ? dayjs(a.created_at).valueOf() : 0;
        const dateB = b.created_at ? dayjs(b.created_at).valueOf() : 0;
        return dateB - dateA;
      }
      if (sortBy === "last_message_at") {
        const dateA = a.last_message_at
          ? dayjs(a.last_message_at).valueOf()
          : 0;
        const dateB = b.last_message_at
          ? dayjs(b.last_message_at).valueOf()
          : 0;
        return dateB - dateA;
      }
      return 0;
    });

  const paginatedConversations = filteredConversations.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const columns = [
    {
      title: "Customer",
      key: "customer",
      render: (record: Conversation) => (
        <div className="customer-cell">
          <div className="id-date-row">
            <span className="ticket-hash">#TK-{record.ticket_no}</span>
            <span className="create-date">
              {formatChicago(record.created_at, "MMM DD, YYYY hh:mm A")}
            </span>
          </div>
          <div className="name-email-row">
            <span className="customer-name">{record.customer_name}</span>
            {record.customer_email && (
              <span className="customer-email">{record.customer_email}</span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Request Info",
      key: "request_info",
      render: (record: Conversation) => (
        <div className="request-info-cell">
          <span className="intent-badge">{record.last_intent}</span>
          <span className="intent-summary">
            {record.last_message?.content || record.summary || "General query"}
          </span>
        </div>
      ),
    },
    {
      title: "Saler",
      key: "handler",
      render: (record: Conversation) => {
        const handlerName =
          record.handler === "bot" ? "Bot" : record.handler || "N/A";
        return <span className="handler-name">{handlerName}</span>;
      },
    },
    {
      title: "Last Message",
      key: "last_message",
      render: (record: Conversation) => {
        const waitingStr = formatLastMessageTime(record.last_message_at);
        const isCustomer = record.last_message?.sender_type === "customer";
        return (
          <div className="last-message-cell">
            <span
              className={`waiting-time ${isCustomer ? "waiting-alert" : ""}`}
            >
              {waitingStr}
            </span>
            <span className="last-sender">
              {isCustomer
                ? "Customer"
                : record.last_message?.sender_type === "bot"
                  ? "Bot"
                  : "Agent"}
            </span>
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      render: (record: Conversation) => {
        let label = "Bot Handling";
        let colorClass = "bot-handling";

        if (record.status === "waiting_for_human") {
          label = "Need Human";
          colorClass = "need-human";
        } else if (record.status === "active") {
          label = "Human Handling";
          colorClass = "human-handling";
        } else if (record.status === "resolved") {
          label = "Resolved";
          colorClass = "resolved";
        }

        return <span className={`status-pill ${colorClass}`}>{label}</span>;
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center" as const,
      render: (record: Conversation) => (
        <Flex gap={12} justify="center">
          <Tooltip title="View Conversation">
            <Button
              type="text"
              className="action-btn view-btn"
              icon={<EyeOutlined size={18} />}
              onClick={() =>
                navigate(`/ticket-detail/${record.conversation_id}`)
              }
            />
          </Tooltip>
          {record.status !== "resolved" && (
            <Tooltip title="Mark as Resolved">
              <Button
                type="text"
                className="action-btn resolve-btn"
                loading={resolvingId === record.conversation_id}
                icon={<CheckOutlined size={18} />}
                onClick={() => handleResolve(record.conversation_id)}
              />
            </Tooltip>
          )}
        </Flex>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Layout contentClassName="ticket-list-page">
        <BreadcrumbBlock
          items={[
            {
              title: (
                <Flex align="center" gap={16}>
                  <Home2 variant="Bulk" />
                  <Link to="/">CMD Tech</Link>
                </Flex>
              ),
            },
            { title: "Store Management" },
            { title: "Conversation Audit" },
          ]}
          title="Conversation Audit"
          description=""
        />

        <div className="ticket-list-container">
          {/* Filters Row */}
          <div className="filters-header-row">
            <div className="search-bar-wrapper">
              <Input
                prefix={<SearchOutlined style={{ color: "#808191" }} />}
                placeholder="Search by customer, issue, saler..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
                onPressEnter={handleSearch}
                allowClear
                className="rs-input search-inbox-input"
              />
            </div>
            <div className="dropdowns-group">
              <Select
                allowClear
                placeholder="Status"
                className="rs-select filter-select"
                popupClassName="rs-select-dropdown"
                value={statusFilter}
                onChange={(val) => {
                  setStatusFilter(val);
                  setCurrentPage(1);
                }}
                suffixIcon={<ArrowSquareDown size={18} variant="Bulk" />}
                options={[
                  { value: "active", label: "Bot Handling" },
                  { value: "waiting_for_human", label: "Need Human" },
                  { value: "human_handling", label: "Human Handling" },
                  { value: "resolved", label: "Resolved" },
                ]}
              />
              <Select
                placeholder="Sort by"
                className="rs-select filter-select"
                popupClassName="rs-select-dropdown"
                value={sortBy}
                onChange={(val) => {
                  setSortBy(val);
                  setCurrentPage(1);
                }}
                suffixIcon={<ArrowSquareDown size={18} variant="Bulk" />}
                options={[
                  { value: "created_at", label: "Sort by: Create at" },
                  { value: "last_message_at", label: "Sort by: Last Message" },
                ]}
              />
            </div>
          </div>

          {/* Conversations Table */}
          <div className="inbox-table-card">
            <Table
              columns={columns}
              dataSource={paginatedConversations}
              rowKey="conversation_id"
              loading={loading}
              pagination={false}
              className="custom-admin-table"
            />

            {/* Pagination */}
            {filteredConversations.length > 0 && (
              <div className="table-pagination-row">
                <Pagination
                  current={currentPage}
                  total={filteredConversations.length}
                  pageSize={pageSize}
                  showSizeChanger
                  onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                  }}
                  onShowSizeChange={(current, size) => {
                    setCurrentPage(1);
                    setPageSize(size);
                  }}
                  className="custom-admin-pagination"
                />
              </div>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
};

export default TicketList;

